// TypeScript port of src/pwhlstats/web/shotmap.py — SVG shot-map rendering.
//
// Mirrors the Python source closely; the output SVG is intentionally
// near-identical so the CSS classes (.shotmap, .shotmap-wrap, .swatch,
// .hex-lo/.hex-hi, [data-zone="slot"]) and the global tooltip handler in
// base.html keep working unchanged.
//
// Row shape (from the play-index query): x_loc, y_loc, zone, xg, is_goal,
// player_name, team_code, game_id, pressure_sequence_tag.

export interface ShotmapRow {
  x_loc: number | null;
  y_loc: number | null;
  zone: string | null;
  xg: number | null;
  is_goal: number | boolean | null;
  player_name: string | null;
  team_code: string | null;
  game_id: number;
  pressure_sequence_tag?: number | boolean | null;
  is_oz?: number | null;
}

const RINK_WIDTH = 600;
const RINK_HEIGHT = 300;
const HALF_WIDTH = 300;
const NET_Y = 150;
const PLOT_SCALE = 1.5;
const PLOT_W = Math.floor(HALF_WIDTH * PLOT_SCALE);
const PLOT_H = Math.floor(RINK_HEIGHT * PLOT_SCALE);

function normalizeXY(x: number | null, y: number | null): [number, number] | null {
  if (x === null || y === null) return null;
  if (!(x >= 0 && x <= RINK_WIDTH && y >= 0 && y <= RINK_HEIGHT)) return null;
  if (x < HALF_WIDTH) x = RINK_WIDTH - x;
  return [x - HALF_WIDTH, y];
}

function rinkSvgOpen(): string {
  const goalX = 289;
  const blueX = 80;
  return (
    `<svg viewBox="0 0 ${HALF_WIDTH} ${RINK_HEIGHT}" ` +
    `width="${PLOT_W}" height="${PLOT_H}" ` +
    `class="shotmap" xmlns="http://www.w3.org/2000/svg">` +
    `<rect x="0" y="0" width="300" height="300" rx="30" ry="30" ` +
    `fill="#ffffff" stroke="#c9ced6" stroke-width="1"/>` +
    `<line x1="0" y1="0" x2="0" y2="300" stroke="#c0392b" stroke-width="2"/>` +
    `<line x1="${blueX}" y1="0" x2="${blueX}" y2="300" stroke="#1f6feb" stroke-width="3"/>` +
    `<line x1="${goalX}" y1="6" x2="${goalX}" y2="294" stroke="#c0392b" stroke-width="1"/>` +
    `<path d="M ${goalX} ${NET_Y - 24} A 24 24 0 0 1 ${goalX} ${NET_Y + 24}" ` +
    `fill="#e6f0ff" stroke="#1f6feb" stroke-width="1"/>` +
    `<circle cx="${goalX - 30}" cy="${NET_Y}" r="15" fill="none" stroke="#c9ced6" stroke-width="0.6"/>` +
    `<rect x="${goalX}" y="${NET_Y - 8}" width="6" height="16" fill="none" stroke="#1b1f24" stroke-width="1"/>`
  );
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const SERIES_PALETTE = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#9467bd",
  "#e377c2",
  "#17becf",
  "#bcbd22",
];

export function seriesPalette(n: number): string[] {
  if (n <= 0) return [];
  if (n <= SERIES_PALETTE.length) return SERIES_PALETTE.slice(0, n);
  return Array.from(
    { length: n },
    (_, i) => SERIES_PALETTE[i % SERIES_PALETTE.length]!,
  );
}

function isDzEvent(r: ShotmapRow): boolean {
  return r.is_oz === 0;
}

export interface BuildDotsOptions {
  gameColors?: Map<number, string>;
  ozOnly?: boolean;
  slotTint?: boolean;
}

export function buildDotsSvg(
  rows: Iterable<ShotmapRow>,
  opts: BuildDotsOptions = {},
): { svg: string; plotted: number } {
  const parts: string[] = [rinkSvgOpen()];
  if (opts.slotTint) {
    parts.push(
      '<rect x="200" y="90" width="85" height="120" ' +
        'fill="#f59e0b" fill-opacity="0.18" stroke="#f59e0b" ' +
        'stroke-opacity="0.45" stroke-width="1" rx="6" ' +
        'data-zone="slot"><title>Slot zone</title></rect>',
    );
  }
  const goalParts: string[] = [];
  const shotParts: string[] = [];
  let plotted = 0;
  for (const r of rows) {
    const norm = normalizeXY(r.x_loc, r.y_loc);
    if (norm === null || r.zone === "behind_net") continue;
    if (opts.ozOnly && isDzEvent(r)) continue;
    const [px, py] = norm;
    const xg = r.xg ?? 0.05;
    const radius = Math.max(3.0, Math.min(12.0, 3 + xg * 9));
    const label = escapeXml(
      `${r.player_name || "—"} · ${r.team_code || "?"} · xG ${xg.toFixed(2)}`,
    );
    const color = opts.gameColors?.get(r.game_id);
    const pressure = !!r.pressure_sequence_tag;
    plotted += 1;
    if (r.is_goal) {
      const fill = color ?? "#c0392b";
      const stroke = pressure ? "#f59e0b" : color ? "#1b1f24" : "#7a1d12";
      const strokeW = pressure ? "1.6" : "1.1";
      const tip = `${pressure ? "PRESSURE · " : ""}GOAL · ${label}`;
      goalParts.push(
        `<circle cx="${px.toFixed(1)}" cy="${py.toFixed(1)}" r="${radius.toFixed(1)}" ` +
          `fill="${fill}" fill-opacity="0.9" stroke="${stroke}" stroke-width="${strokeW}" ` +
          `data-tip="${tip}">` +
          `<title>${tip}</title></circle>`,
      );
    } else {
      const fill = color ?? "#0a6cf1";
      const strokeAttr = pressure
        ? 'stroke="#f59e0b" stroke-width="1.4"'
        : 'stroke="none"';
      const tip = `${pressure ? "PRESSURE · " : ""}${label}`;
      shotParts.push(
        `<circle cx="${px.toFixed(1)}" cy="${py.toFixed(1)}" r="${radius.toFixed(1)}" ` +
          `fill="${fill}" fill-opacity="0.4" ${strokeAttr} ` +
          `data-tip="${tip}">` +
          `<title>${tip}</title></circle>`,
      );
    }
  }
  parts.push(...shotParts, ...goalParts, "</svg>");
  return { svg: parts.join(""), plotted };
}

// ---- hexbin -----------------------------------------------------------------

const HEX_R = 18.0;

function hexPolygon(cx: number, cy: number, r: number): string {
  const pts: string[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 2;
    pts.push(
      `${(cx + r * Math.cos(angle)).toFixed(2)},${(cy + r * Math.sin(angle)).toFixed(2)}`,
    );
  }
  return pts.join(" ");
}

function colorForCount(count: number, maxCount: number): string {
  if (maxCount <= 0) return "#eef5ff";
  const t = count / maxCount;
  const r = Math.floor(238 + (192 - 238) * t);
  const g = Math.floor(245 + (57 - 245) * t);
  const b = Math.floor(255 + (43 - 255) * t);
  return `rgb(${r},${g},${b})`;
}

export function buildHexbinSvg(
  rows: Iterable<ShotmapRow>,
  opts: { ozOnly?: boolean } = {},
): string {
  type Bin = { count: number; goals: number; xgSum: number };
  const bins = new Map<string, Bin>();
  const w = HEX_R * Math.sqrt(3);
  const h = HEX_R * 1.5;
  for (const r of rows) {
    const norm = normalizeXY(r.x_loc, r.y_loc);
    if (norm === null || r.zone === "behind_net") continue;
    if (opts.ozOnly && isDzEvent(r)) continue;
    const [px, py] = norm;
    const row = Math.round(py / h);
    const off = row % 2 ? w / 2 : 0;
    const col = Math.round((px - off) / w);
    const key = `${col},${row}`;
    let bin = bins.get(key);
    if (!bin) {
      bin = { count: 0, goals: 0, xgSum: 0.0 };
      bins.set(key, bin);
    }
    bin.count += 1;
    if (r.is_goal) bin.goals += 1;
    if (r.xg !== null) bin.xgSum += r.xg ?? 0;
  }
  const parts: string[] = [rinkSvgOpen()];
  if (bins.size === 0) {
    parts.push("</svg>");
    return parts.join("");
  }
  let maxCount = 0;
  for (const b of bins.values()) if (b.count > maxCount) maxCount = b.count;
  for (const [key, b] of bins) {
    const [col, row] = key.split(",").map(Number) as [number, number];
    const off = row % 2 ? w / 2 : 0;
    const cx = col * w + off;
    const cy = row * h;
    if (!(-HEX_R <= cx && cx <= HALF_WIDTH + HEX_R && -HEX_R <= cy && cy <= RINK_HEIGHT + HEX_R)) {
      continue;
    }
    const color = colorForCount(b.count, maxCount);
    const tooltip = escapeXml(
      `${b.count} shots · ${b.goals} goals · xG ${b.xgSum.toFixed(2)}`,
    );
    parts.push(
      `<polygon points="${hexPolygon(cx, cy, HEX_R - 0.5)}" ` +
        `fill="${color}" fill-opacity="0.85" stroke="#ffffff" stroke-width="0.5">` +
        `<title>${tooltip}</title></polygon>`,
    );
  }
  parts.push("</svg>");
  return parts.join("");
}
